import { router, usePage } from "@inertiajs/react"
import { Eye } from "lucide-react"
import { useEffect } from "react"

import { NativeForm } from "@/components/native-form"
import { Button } from "@/components/ui/button"
import { showToast } from "@/lib/toast"
import { frontUsersSessions } from "@/routes"

/**
 * Stops visits that would change data while the account is read-only (an admin view or the showcase),
 * and says why, instead of sending them to the server for a 403.
 */
export function useReadOnlyGuard() {
  const { auth, admin_view: adminView } = usePage().props
  const message = adminView
    ? "This admin view is read-only. Changes are disabled."
    : auth?.user.showcase
      ? "This showcase account is read-only. Changes are disabled."
      : null

  useEffect(() => {
    if (!message) return

    return router.on("before", (event) => {
      const { method, url } = event.detail.visit
      if (method === "get") return
      // Showcase visitors may sign out; an admin exits the view instead.
      if (!adminView && url.pathname === frontUsersSessions.destroy().url) return

      event.preventDefault()
      showToast("warning", message)
    })
  }, [message, adminView])
}

/** Marks an admin's read-only view of a client's account, with the way out. Renders nothing otherwise. */
export function AdminViewBadge() {
  const adminView = usePage().props.admin_view
  if (!adminView) return null

  const endsAt = new Date(adminView.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })

  return (
    <div
      role="status"
      // Phones have no room in the navbar: the badge floats at the bottom of the screen instead.
      className="fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-full border border-warning-border bg-warning-soft py-1 pr-1 pl-3 text-sm text-warning-strong shadow-lg sm:static sm:mr-2 sm:shadow-none"
      title={`Viewing as ${adminView.user_email} until ${endsAt}`}
    >
      <Eye className="size-4 shrink-0" aria-hidden="true" />
      <span className="hidden max-w-48 truncate sm:inline">{adminView.account_name ?? adminView.user_email}</span>
      <span className="font-medium">Read-only</span>
      <NativeForm route={{ url: adminView.exit_url, method: "delete" }}>
        <Button type="submit" size="xs" variant="outline" className="rounded-full border-warning-border bg-card text-warning-strong hover:bg-warning-soft hover:text-warning-strong">
          Exit
        </Button>
      </NativeForm>
    </div>
  )
}
